import type { MouseEvent as ReactMouseEvent } from 'react';

/**
 * The shift-range press on a manage grid's card, handled by us rather than by the `<label>`
 * (2026-08-02).
 *
 * A card in manage mode is one `<label>` around its checkbox, which is the right shape: the whole card
 * is one target, and the association is the browser's rather than ours. But **a label does not forward a
 * shift-modified click to its control**, and it took two engines to see the whole of that:
 *
 * - **Blink** forwards it and the forwarded click carries `shiftKey` — but only once nothing inside the
 *   card is selectable. A shift-press on selectable text is read as extending the document selection and
 *   the activation behaviour is then declined (measured 2026-08-02; `user-select: none` on the card is
 *   what fixed that, and it stays).
 * - **Gecko declines it regardless.** Measured on Firefox 153 with `user-select: none` already in place
 *   and the selection provably empty: a shifted press produces the label's own `click`
 *   (`target=SPAN`, `shiftKey=true`) and then *nothing* — no forwarded click, no `change`, the box
 *   unchanged. A plain press on the same spot forwards normally. So there is no selection to blame and
 *   no modifier to read: the event the checkbox would have answered never exists.
 *
 * Hence: the label's own click is where a shifted press is answered, and `preventDefault()` is what keeps
 * the engine that *would* forward it from toggling a second time — cancelling a click cancels the
 * activation behaviour, which is exactly the forwarding. Measured both ways: in Blink the forwarded
 * `input click`/`change` pair disappears and the box is left to us; in Gecko there was nothing to cancel.
 * An unmodified press is untouched in both, so the ordinary path stays the browser's.
 *
 * A press that lands **on the checkbox itself** is left alone: the control's own `change` fires there in
 * both engines and carries the modifier, and cancelling a click on a checkbox would cancel the check.
 */
export function shiftRangePress(
  onToggle: (range: boolean) => void,
): (event: ReactMouseEvent<HTMLLabelElement>) => void {
  return (event) => {
    if (!event.shiftKey) return;
    if (event.target instanceof HTMLInputElement) return;
    event.preventDefault();
    onToggle(true);
  };
}
